import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

/**
 * Imprint/Impressum Page
 * Legal information about Emporion Innovations GmbH
 * Serves both /impressum (DE) and /imprint (EN)
 */
const ImprintPage = () => {
  const { t, i18n } = useTranslation('legal')
  const isGerman = i18n.language === 'de'

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-purple-600 transition-colors mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {isGerman ? 'Zurück' : 'Back'}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isGerman ? 'Impressum' : 'Imprint'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isGerman
              ? 'Angaben gemäß § 5 TMG'
              : 'Information according to § 5 TMG (German Telemedia Act)'}
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8 space-y-8">

            {/* Company Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Angaben zum Unternehmen' : 'Company Information'}
              </h2>
              <div className="space-y-2 text-gray-700">
                <p className="font-medium text-lg">Emporion Innovations GmbH</p>
                <p>Ludwigstr. 32</p>
                <p>63067 Offenbach</p>
                <p>{isGerman ? 'Deutschland' : 'Germany'}</p>
              </div>
            </section>

            {/* Legal Representatives */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Vertreten durch' : 'Represented by'}
              </h2>
              <div className="space-y-2 text-gray-700">
                <p>{isGerman ? 'Geschäftsführer' : 'Managing Directors'}:</p>
                <p className="font-medium">Alexander Kuchta</p>
                <p className="font-medium">Dennis Schmitz</p>
              </div>
            </section>

            {/* Contact */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Kontakt' : 'Contact'}
              </h2>
              <div className="space-y-2 text-gray-700">
                <p>
                  <span className="font-medium">{isGerman ? 'Telefon' : 'Phone'}:</span>{' '}
                  <a href="tel:+4906999998088" className="text-purple-600 hover:text-purple-700">
                    +49 (0) 69 99998088
                  </a>
                </p>
                <p>
                  <span className="font-medium">E-Mail:</span>{' '}
                  <a href="mailto:info@emporion.technology" className="text-purple-600 hover:text-purple-700">
                    info@emporion.technology
                  </a>
                </p>
              </div>
            </section>

            {/* Register Entry */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Registereintrag' : 'Register Entry'}
              </h2>
              <div className="space-y-2 text-gray-700">
                <p>
                  <span className="font-medium">
                    {isGerman ? 'Eintragung im Handelsregister' : 'Entry in the Commercial Register'}:
                  </span>
                </p>
                <p>
                  {isGerman ? 'Registergericht' : 'Registry Court'}: Offenbach am Main
                </p>
                <p>
                  {isGerman ? 'Registernummer' : 'Registration Number'}: HRB 51283
                </p>
              </div>
            </section>

            {/* Tax Information */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Umsatzsteuer-ID' : 'VAT Identification Number'}
              </h2>
              <div className="space-y-2 text-gray-700">
                <p>
                  {isGerman
                    ? 'Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:'
                    : 'VAT identification number according to § 27 a of the German VAT Act:'}
                </p>
                <p className="font-medium">DE284997944</p>
              </div>
            </section>

            {/* EU Dispute Resolution */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman
                  ? 'Streitschlichtung'
                  : 'EU Dispute Resolution'}
              </h2>
              <div className="space-y-2 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:'
                    : 'The European Commission provides a platform for online dispute resolution (ODR):'}
                </p>
                <p>
                  <a
                    href="https://ec.europa.eu/consumers/odr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 break-all"
                  >
                    https://ec.europa.eu/consumers/odr
                  </a>
                </p>
                <p className="mt-4">
                  {isGerman
                    ? 'Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.'
                    : 'We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.'}
                </p>
              </div>
            </section>

            {/* Liability Disclaimer */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Haftungsausschluss' : 'Liability Disclaimer'}
              </h2>
              <div className="space-y-4 text-gray-700 text-sm">

                {/* Content Liability */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {isGerman ? 'Haftung für Inhalte' : 'Liability for Content'}
                  </h3>
                  <p>
                    {isGerman
                      ? 'Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.'
                      : 'As a service provider, we are responsible for our own content on these pages in accordance with § 7 para.1 TMG under general law. According to §§ 8 to 10 TMG, we are not obligated to monitor transmitted or stored external information or to research circumstances that indicate illegal activity.'}
                  </p>
                </div>

                {/* Link Liability */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {isGerman ? 'Haftung für Links' : 'Liability for Links'}
                  </h3>
                  <p>
                    {isGerman
                      ? 'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.'
                      : 'Our offer contains links to external third-party websites over whose content we have no influence. Therefore, we cannot accept any liability for this external content. The respective provider or operator of the pages is always responsible for the content of the linked pages.'}
                  </p>
                </div>

                {/* Copyright */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {isGerman ? 'Urheberrecht' : 'Copyright'}
                  </h3>
                  <p>
                    {isGerman
                      ? 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.'
                      : 'The content and works created by the site operators on these pages are subject to German copyright law. The reproduction, editing, distribution and any kind of exploitation outside the limits of copyright require the written consent of the respective author or creator.'}
                  </p>
                </div>
              </div>
            </section>

          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
            <Link to={isGerman ? '/datenschutz' : '/privacy'} className="hover:text-purple-600">
              {isGerman ? 'Datenschutz' : 'Privacy Policy'}
            </Link>
            <Link to="/terms" className="hover:text-purple-600">
              {isGerman ? 'AGB' : 'Terms of Service'}
            </Link>
            <Link to="/contact" className="hover:text-purple-600">
              {isGerman ? 'Kontakt' : 'Contact'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImprintPage
